import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import "./LeavingCertificate.css";

import {
  baseUrl,
  formatDateToDDMMYYYY,
  currentDate,
  LEAVING_CERTIFICATE,
  formatLCVersion
} from "../../Utility/constants";

import axios from "axios";


const LeavingCertificate = () => {

  // =========================================================
  // ROUTE PARAMETERS
  // =========================================================

  const {
    studentId,
    currentVersion,
    currentCertificateNumber,
    certificateId
  } = useParams();

  const location = useLocation();

  const queryParams =
    new URLSearchParams(location.search);

  const certType =
    queryParams.get("certType") || "Original";


  // =========================================================
  // STATE
  // =========================================================

  const [schoolId, setSchoolId] = useState("");

  const [certificateData, setCertificateData] =
    useState([]);

  const [schoolData, setSchoolData] =
    useState([]);

  const hasCalled = useRef(false);


  // =========================================================
  // UPDATE CERTIFICATE
  // =========================================================

  useEffect(() => {

    const updateData = async () => {

      await updateCertificate();

    };

    updateData();

  }, []);


  // =========================================================
  // FETCH STUDENT DATA
  // =========================================================

  useEffect(() => {

    const fetchCertificateData = async () => {

      try {

        const response = await axios.get(
          `${baseUrl}/api/PersonalDetails/ReturnStudentDetails?studentId=${studentId}`
        );

        if (
          response &&
          response.data
        ) {

          setCertificateData(
            response.data
          );

          if (
            response.data.schoolId
          ) {

            setSchoolId(
              response.data.schoolId
            );

          }

        }

      } catch (error) {

        console.log(
          "Failed to fetch student details. Please try again.",
          error
        );

      }

    };

    fetchCertificateData();

  }, [studentId]);


  // =========================================================
  // UPDATE LC VERSION
  // =========================================================

  useEffect(() => {

    const updateLCVersion = async () => {

      if (hasCalled.current) {
        return;
      }

      hasCalled.current = true;


      const apiVersion =
        formatLCVersion(
          currentVersion
        ).split("-")[1];


      const data = {

        studentId:
          studentId,

        certificateName:
          LEAVING_CERTIFICATE,

        version:
          currentVersion.toLowerCase() ===
          "original"
            ? 1
            : parseInt(apiVersion) + 1

      };


      try {

        await axios.post(
          `${baseUrl}/api/Numbering`,
          data
        );

      } catch (error) {

        console.error(
          "Error sending data:",
          error
        );

      }

    };

    updateLCVersion();

  }, [currentVersion, studentId]);


  // =========================================================
  // UPDATE CERTIFICATE NUMBER
  // =========================================================

  const updateCertificate = async () => {

    const data = {

      CertificateNo:
        currentCertificateNumber,

      studentId:
        parseInt(studentId),

      usedBy:
        localStorage.getItem("userID"),

      certificateName:
        LEAVING_CERTIFICATE,

      Id:
        parseInt(certificateId),

      certType:
        (
          queryParams.get("certType") ||
          ""
        ).trim()

    };


    try {

      await axios.put(
        `${baseUrl}/api/Numbering`,
        data
      );

    } catch (error) {

      console.error(
        "Error sending data:",
        error
      );

    }

  };


  // =========================================================
  // FETCH SCHOOL DATA
  // =========================================================

  useEffect(() => {

    if (schoolId) {

      const fetchSchoolData = async () => {

        try {

          const response =
            await axios.get(
              `${baseUrl}/api/SchoolGeneralInfo/GetSchoolInfo?schoolId=${schoolId}`
            );


          if (
            response &&
            response.data
          ) {

            setSchoolData(
              response.data
            );

          }

        } catch (error) {

          console.log(
            "Failed to fetch school details. Please try again.",
            error
          );

        }

      };

      fetchSchoolData();

    }

  }, [schoolId]);


  // =========================================================
  // DATE ONLY
  // =========================================================

  const getDateOnly = (date) => {

    if (
      !date ||
      typeof date !== "string"
    ) {

      return "";

    }

    return date.split("T")[0];

  };


  // =========================================================
  // DYNAMIC SERIAL NUMBER
  // =========================================================
  //
  // IMPORTANT:
  //
  // We don't use:
  //
  // <td>1</td>
  // <td>2</td>
  // <td>3</td>
  //
  // anymore.
  //
  // Every visible row uses:
  //
  // serialNo++
  //
  // Therefore hidden rows automatically disappear
  // from the numbering.
  // =========================================================

  let serialNo = 1;


  // =========================================================
  // RENDER
  // =========================================================

  return (

    <div className="certificate-container">


      {/* =====================================================
          SCHOOL HEADER
      ===================================================== */}

      <div>

        <table className="header-table">

          <tbody>

            <tr>

              {/* SCHOOL LOGO */}

              <td
                style={{
                  width: "200px",
                  verticalAlign: "middle",
                  textAlign: "center"
                }}
              >

                <img
                  src={schoolData.logo}
                  className="responsive"
                  width="150"
                  alt="School Logo"
                />

              </td>


              {/* SCHOOL INFORMATION */}

              <td
                style={{
                  borderRight: "none"
                }}
              >

                <b
                  style={{
                    fontSize: "15px"
                  }}
                >

                  {schoolData.organizationName}

                </b>

                <br />


                <b
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold"
                  }}
                >

                  {schoolData.schoolOrCollegeName}

                </b>

                <br />


                <b>
                  (Affiliated to H.S.C. Board,
                  Govt. Of Maharashtra)
                </b>

                <br />


                Email :

                {" "}

                <b>
                  {schoolData.email}
                </b>

                <br />


                U-DISE No :

                {" "}

                <b>
                  {schoolData.udiseNo}
                </b>

                {" | "}


                {(schoolData.ssC10thIndexNo ||
                  schoolData.hsC12thIndexNo) && (

                  <>

                    Index No :

                    {" "}

                    <b>

                      {schoolData.ssC10thIndexNo}

                      {schoolData.ssC10thIndexNo &&
                        schoolData.hsC12thIndexNo &&
                        " | "}

                      {schoolData.hsC12thIndexNo}

                    </b>

                  </>

                )}

              </td>


              <td
                style={{
                  width: "200px",
                  borderLeft: "none"
                }}
              />

            </tr>

          </tbody>

        </table>


        {/* =================================================
            RECOGNITION NUMBER
        ================================================= */}

        <table className="header-table">

          <tbody>

            <tr>

              <td
                style={{
                  width: "50%"
                }}
              >

                School / College Recognition No.

                <b>

                  {" : "}

                  {schoolData.schoolRegistrationNumber &&
                    schoolData.schoolRegistrationNumber}


                  {schoolData.schoolRegistrationNumber &&
                    schoolData.collegeRegistrationNumber &&
                    " | "}


                  {schoolData.collegeRegistrationNumber &&
                    schoolData.collegeRegistrationNumber}

                </b>

              </td>

            </tr>

          </tbody>

        </table>

      </div>


      {/* =====================================================
          TITLE
      ===================================================== */}

      <div
        className="certificate-title mt-2 text-center"
        style={{
          position: "relative"
        }}
      >

        <h5
          className="title d-inline-block mx-2 mt-4"
        >
          LEAVING CERTIFICATE
        </h5>

      </div>


      {/* =====================================================
          CERTIFICATE TYPE
      ===================================================== */}

      <div
        className="mt-1"
        style={{
          position: "relative"
        }}
      >

        <span
          style={{
            color: "red",
            fontWeight: "bold",
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)"
          }}
        >

          {certificateData.studentType ===
          "17 No (Private/External)"

            ? "Private"

            :

            <p
              style={{
                color:
                  certType === "Original"
                    ? "green"
                    : "red",
                textTransform: "uppercase"
              }}
            >

              {certType}

            </p>

          }

        </span>


        <span
          style={{
            fontWeight: "bold",
            textAlign: "right",
            display: "block"
          }}
        >

          Certificate No:
          {" "}
          {currentCertificateNumber}

        </span>

      </div>


      {/* =====================================================
          CERTIFICATE BODY
      ===================================================== */}

      <div className="certificate-body">

        <div className="certificate-section">

          <table className="certificate-table">

            <tbody>


              {/* =================================================
                  1. GENERAL REGISTER NO
              ================================================= */}

              <tr>

                <td>{serialNo++}</td>

                <td>
                  General Register No
                </td>

                <td>

                  {certificateData.generalRegisterNo
                    ? certificateData.generalRegisterNo
                    : "N/A"}

                </td>

              </tr>


              {/* =================================================
                  2. STUDENT ID
              ================================================= */}

              <tr>

                <td>{serialNo++}</td>

                <td>
                  Student ID (SARAL)
                </td>

                <td>

                  {certificateData.studentSaralId
                    ? certificateData.studentSaralId
                    : "N/A"}

                </td>

              </tr>


              {/* =================================================
                  3. APAAR ID
              ================================================= */}

              <tr>

                <td>{serialNo++}</td>

                <td>
                  APAAR ID
                </td>

                <td>

                  {certificateData.apaarId
                    ? certificateData.apaarId
                    : "N/A"}

                </td>

              </tr>


              {/* =================================================
                  4. NATIONAL CODE
              ================================================= */}

              <tr>

                <td>{serialNo++}</td>

                <td>
                  National Code (PEN No)
                </td>

                <td>

                  {certificateData.permanentEducationNumber
                    ? certificateData.permanentEducationNumber
                    : "N/A"}

                </td>

              </tr>


              {/* =================================================
                  5. UID
              ================================================= */}

              <tr>

                <td>{serialNo++}</td>

                <td>
                  UID (Aadhar No)
                </td>

                <td>

                  {certificateData.uidNo
                    ? certificateData.uidNo
                    : "N/A"}

                </td>

              </tr>


              {/* =================================================
                  6. STUDENT NAME
              ================================================= */}

              <tr>

                <td>{serialNo++}</td>

                <td>
                  Name of the student (Full Name)
                </td>

                <td>

                  {schoolData.lcNamingPrint

                    ?

                    certificateData.lastName +
                    " " +
                    certificateData.firstName +
                    " " +
                    certificateData.middleName

                    :

                    certificateData.firstName +
                    " " +
                    certificateData.middleName +
                    " " +
                    certificateData.lastName

                  }

                </td>

              </tr>


              {/* =================================================
                  7. MOTHER'S NAME
              ================================================= */}

              <tr>

                <td>{serialNo++}</td>

                <td>
                  Mother's Name
                </td>

                <td>

                  {certificateData.motherFullName
                    ? certificateData.motherFullName
                    : "N/A"}

                </td>

              </tr>


              {/* =================================================
                  RELIGION / CASTE
              ================================================= */}

              {!(
                certificateData.hideReligion === true &&
                certificateData.hideCaste === true
              ) && (

                <tr>

                  <td>
                    {serialNo++}
                  </td>


                  <td>

                    {certificateData.hideReligion === false &&
                      "Religion"}

                    {certificateData.hideReligion === false &&
                      certificateData.hideCaste === false &&
                      " & "}

                    {certificateData.hideCaste === false &&
                      "Caste"}

                  </td>


                  <td>

                    {certificateData.hideReligion === false &&
                      certificateData.religion
                      ? certificateData.religion
                      : ""}


                    {certificateData.hideReligion === false &&
                      certificateData.hideCaste === false &&
                      " "}


                    {certificateData.hideCaste === false &&
                      certificateData.caste
                      ? certificateData.caste
                      : ""}

                  </td>

                </tr>

              )}


              {/* =================================================
                  NATIONALITY
              ================================================= */}

              {certificateData.hideNationality !== true && (

                <tr>

                  <td>
                    {serialNo++}
                  </td>

                  <td>
                    Nationality
                  </td>

                  <td>

                    {certificateData.nationality
                      ? certificateData.nationality
                      : "N/A"}

                  </td>

                </tr>

              )}


              {/* =================================================
                  MOTHER TONGUE
              ================================================= */}

              {certificateData.hideMotherTongue !== true && (

                <tr>

                  <td>
                    {serialNo++}
                  </td>

                  <td>
                    Mother Tongue
                  </td>

                  <td>

                    {certificateData.motherTongue
                      ? certificateData.motherTongue
                      : "N/A"}

                  </td>

                </tr>

              )}


              {/* =================================================
                  PLACE OF BIRTH
              ================================================= */}

              {certificateData.hideBirthPlace !== true && (

                <tr>

                  <td>
                    {serialNo++}
                  </td>

                  <td>
                    Place of Birth
                    {" "}
                    (Village/City, Tal., Zilla, State, Country):
                  </td>

                  <td>

                    {certificateData.birthPlace
                      ? certificateData.birthPlace
                      : "N/A"}

                  </td>

                </tr>

              )}


              {/* =================================================
                  DATE OF BIRTH
              ================================================= */}

              <tr>

                <td>
                  {serialNo++}
                </td>

                <td>
                  Date of Birth (Words and Figures)
                </td>

                <td>

                  {getDateOnly(
                    certificateData?.dateOfBirth
                  )

                    ?

                    formatDateToDDMMYYYY(
                      getDateOnly(
                        certificateData?.dateOfBirth
                      )
                    )

                    :

                    "-"

                  }

                  {" "}

                  (
                  {certificateData.dateOfBirthInWords}
                  )

                </td>

              </tr>


              {/* =================================================
                  LAST SCHOOL
              ================================================= */}

              <tr>

                <td>
                  {serialNo++}
                </td>

                <td>
                  Last School/College attended
                </td>

                <td>

                  {certificateData.previousSchoolName
                    ? certificateData.previousSchoolName
                    : "N/A"}

                </td>

              </tr>


              {/* =================================================
                  DATE OF ADMISSION
              ================================================= */}

              <tr>

                <td>
                  {serialNo++}
                </td>

                <td>
                  Date of Admission & Class/Stream
                </td>

                <td>

                  {getDateOnly(
                    certificateData?.admissionDate
                  )

                    ?

                    formatDateToDDMMYYYY(
                      getDateOnly(
                        certificateData?.admissionDate
                      )
                    )

                    :

                    "-"

                  }

                  {" - "}


                  {certificateData.standardName
                    ? certificateData.standardName
                    : "N/A"}

                  {"/"}

                  {certificateData.stream
                    ? certificateData.stream
                    : "N/A"}

                </td>

              </tr>


              {/* =================================================
                  PROGRESS
              ================================================= */}

              <tr>

                <td>
                  {serialNo++}
                </td>

                <td>
                  Progress
                </td>

                <td>

                  {certificateData.lcProgress === ""
                    ? "-"
                    : certificateData.lcProgress}

                </td>

              </tr>


              {/* =================================================
                  CONDUCT
              ================================================= */}

              <tr>

                <td>
                  {serialNo++}
                </td>

                <td>
                  Conduct
                </td>

                <td>

                  {certificateData.lcConduct === ""
                    ? "-"
                    : certificateData.lcConduct}

                </td>

              </tr>


              {/* =================================================
                  DATE OF LEAVING
              ================================================= */}

              <tr>

                <td>
                  {serialNo++}
                </td>

                <td>
                  Date of Leaving the School/College
                </td>

                <td>

                  {getDateOnly(
                    certificateData?.earlyReleaseDate
                  )

                    ?

                    formatDateToDDMMYYYY(
                      getDateOnly(
                        certificateData?.earlyReleaseDate
                      )
                    )

                    :

                    `31st May ${new Date().getFullYear()}`

                  }

                </td>

              </tr>


              {/* =================================================
                  STANDARD IN WHICH STUDYING
              ================================================= */}

              <tr>

                <td>
                  {serialNo++}
                </td>

                <td>
                  Standard in which studying & since when
                </td>

                <td>

                  {certificateData.standardName
                    ? certificateData.standardName
                    : "N/A"}

                  {" "}

                  {certificateData.stream
                    ? certificateData.stream
                    : "N/A"}

                  {" since "}

                  {certificateData.admissionDate &&
                    certificateData.admissionDate.split("-")[0]}

                </td>

              </tr>


              {/* =================================================
                  REASON FOR LEAVING
              ================================================= */}

              <tr>

                <td>
                  {serialNo++}
                </td>

                <td>
                  Reason for leaving
                </td>

                <td>

                  {certificateData.lcReason === ""
                    ? "-"
                    : certificateData.lcReason}

                </td>

              </tr>


              {/* =================================================
                  REMARKS
              ================================================= */}

              <tr>

                <td>
                  {serialNo++}
                </td>

                <td>
                  Remarks
                </td>

                <td>

                  {certificateData.lcRemark === ""
                    ? "-"
                    : certificateData.lcRemark}

                </td>

              </tr>


              {/* =================================================
                  CERTIFICATION
              ================================================= */}

              <tr>

                <td
                  colSpan={3}
                  style={{
                    textAlign: "left",
                    fontWeight: "normal"
                  }}
                >

                  <p>

                    Certified that the above information is
                    in accordance with the School/College
                    General register.

                  </p>

                </td>

              </tr>


            </tbody>

          </table>

        </div>


        {/* =====================================================
            FOOTER
        ===================================================== */}

        <div className="certificate-footer">

          <div className="signatures_LC">

            <div>
              Date: {currentDate()}
            </div>

            <div>
              Class Teacher
            </div>

            <div>
              Clerk
            </div>

            <div>
              Headmaster/Principal
            </div>

          </div>


          <div className="d-block text-start mt-1">

            Note:

            <br />

            1. Unauthorized change in school leaving
            certificate will lead to legal action against
            the concerned.

            <br />

            2. Government Decision - S.No.Sankirna -
            2316 / (229/16) SD.4

          </div>

        </div>

      </div>

    </div>

  );

};


export default LeavingCertificate;